import {SettingsModel, Sections} from "../../settings/models/settings";
import {backboneViewProperties} from "../../decorators/backbone-properties";
import {autoinject} from "aurelia-dependency-injection";
import {JurisdictionSettingsView} from "../../settings/views/jurisdiction-settings";
import {SourceLinkSettingsView} from "../../settings/views/source-link-settings";
import Marionette from "../../shared/marionette-mustache";
import {AppEvents} from "../../app-events";
import { SearchSettingsView } from "./search-settings";

const template = require<string>("../../settings/views/settings.html");

type browserEvent = "click" | "transitionend" | "webkitTransitionEnd" | "oTransitionEnd" | "msTransitionEnd";

const dom = {
    JURISDICTION_BUTTON: "#jurisdiction-button",
    SOURCELINK_BUTTON: "#sourcelink-customisation-button",
    CLOSE_BUTTON: ".settings-close",
    RETURN_BUTTON: ".settings-return",
    SETTINGS_BUTTON: "#settings-button",
    SEARCH_BUTTON: "#search-settings-button",
    SETTINGS_MENU: "#settings-first",
    SETTINGS_PANELS: ".settings-panel",
    JURISDICTIONS_REGION: "#jurisdictions-region",
    SETTINGS_CONTAINER: "#settings-container",
    SOURCELINK_REGION: "#sourcelink-region",
    SEARCH_SETTINGS_REGION: "#search-settings-region",
    BACK_LINK: ".sourcelink-back-link",
    SOURCELINK_SETTINGS: "#sourcelink-settings",
    SEARCH_SETTINGS: "#search-settings",
    JURISDICTION_SETTINGS: "#jurisdiction-settings",
    MOBILE_SIGNOUT_BUTTON: "#mobile-signout-button",
    JURISDICTION_BACK_LINK: "#jurisdiction-return"
};

@autoinject
@backboneViewProperties({
    template: template
})
export class SettingsView extends Marionette.LayoutView<SettingsModel> {

    private jurisdictionsRegion: Marionette.Region;
    private sourceLinkRegion: Marionette.Region;
    private searchSettingsRegion: Marionette.Region;

    constructor(
            private appEvents: Backbone.Events,
            public model: SettingsModel,
            public jurisdictionsView: JurisdictionSettingsView,
            public sourceLinkView: SourceLinkSettingsView,
            public searchSettingsView: SearchSettingsView  ) {
        super();
    }

    public initialize() {
        // Initialize is required by the TypeScript definition.
    }

    public events(): Backbone.EventsHash {
        const events: Backbone.EventsHash = {};
        this.addEvent(events, "click", dom.JURISDICTION_BUTTON, this.jurisdictionLinkClick);
        this.addEvent(events, "click", dom.SEARCH_BUTTON, this.searchButtonClick);
        this.addEvent(events, "click", dom.SOURCELINK_BUTTON, this.sourcelinkClick);
        this.addEvent(events, "click", dom.SETTINGS_BUTTON, this.settingsCloseClick);
        this.addEvent(events, "click", dom.CLOSE_BUTTON, this.settingsCloseClick);
        this.addEvent(events, "click", dom.RETURN_BUTTON, this.backClick);
        this.addEvent(events, "click", dom.BACK_LINK, this.backClick);
        this.addEvent(events, "click", dom.JURISDICTION_BACK_LINK, this.backClick);
        this.addEvent(events, "click", dom.MOBILE_SIGNOUT_BUTTON, this.mobileSignOutClick);
        this.addEvent(events, "transitionend", dom.SETTINGS_CONTAINER, this.settingsPanelTransitionEnded);
        this.addEvent(events, "webkitTransitionEnd", dom.SETTINGS_CONTAINER, this.settingsPanelTransitionEnded);
        this.addEvent(events, "oTransitionEnd", dom.SETTINGS_CONTAINER, this.settingsPanelTransitionEnded);
        this.addEvent(events, "msTransitionEnd", dom.SETTINGS_CONTAINER, this.settingsPanelTransitionEnded);
        return events;
    }

    public regions(): any {
        const regions: any = {
            jurisdictionsRegion: dom.JURISDICTIONS_REGION,
            sourceLinkRegion: dom.SOURCELINK_REGION,
            searchSettingsRegion: dom.SEARCH_SETTINGS_REGION
        };
        return regions;
    }

    public onShow(): void {
        this.jurisdictionsRegion.show(this.jurisdictionsView);
        this.sourceLinkRegion.show(this.sourceLinkView);
        this.searchSettingsRegion.show(this.searchSettingsView);
        this.setupModelChangeListeners();
    }

    public resetViews() {
        this.jurisdictionsRegion.empty({preventDestroy: true});
        this.sourceLinkRegion.empty({preventDestroy: true});
        this.jurisdictionsRegion.show(this.jurisdictionsView);
        this.sourceLinkRegion.show(this.sourceLinkView);
    }

    private setupModelChangeListeners() {
        this.listenTo(this.model, "change:Open", this.openChanged);
        this.listenTo(this.model, "change:Section", this.sectionChanged);
    }
    private mobileSignOutClick(e): void {
        e.stopPropagation();
        const signoutUrl = `signout`;
        Backbone.history.navigate(signoutUrl, { trigger: true });
    }

    private openChanged(model: SettingsModel, prop: boolean) {
        if (prop) {
            this.$el.show();
            this.$(dom.SETTINGS_MENU).find("li a").first().focus();
            // defer this, so that showing this element doesn"t bowl over the transition that applying the open class normally gives us
            _.defer(() => {
                this.$el.parent().toggleClass("open", prop);
            });
        } else {
            this.$el.parent().toggleClass("open", prop);
            this.model.Section = Sections.MENU;
            (this.sourceLinkRegion as any).$el.removeAttr("style");
        }
    }

    private showMenu() {
        this.$(dom.SETTINGS_CONTAINER).removeClass();
        this.$(dom.SETTINGS_MENU).find("li a").first().focus();
    }

    private showJurisdictionSettings() {
        this.$(dom.SETTINGS_CONTAINER).removeClass().addClass("level-1");
        this.$(dom.SOURCELINK_SETTINGS).hide();
        this.$(dom.SEARCH_SETTINGS).hide();
        this.$(dom.JURISDICTION_SETTINGS).show();
    }

    private showSourceLinkSettings() {
        this.$(dom.SETTINGS_CONTAINER).removeClass().addClass("level-1");
        this.$(dom.JURISDICTION_SETTINGS).hide();
        this.$(dom.SEARCH_SETTINGS).hide();
        this.$(dom.SOURCELINK_SETTINGS).show();
    }

    private showSearchSettings() {
        this.$(dom.SETTINGS_CONTAINER).removeClass().addClass("level-1");
        this.$(dom.JURISDICTION_SETTINGS).hide();
        this.$(dom.SOURCELINK_SETTINGS).hide();
        this.$(dom.SEARCH_SETTINGS).show();
    }

    private sectionChanged(): void {
        const section = this.model.Section;
        switch (section) {
            case Sections.MENU:
                this.showMenu();
                break;
            case Sections.JURISDICTIONS:
                this.showJurisdictionSettings();
                break;
            case Sections.SOURCELINKS:
                this.showSourceLinkSettings();
                break;
            case Sections.SEARCH_SETTINGS:
                this.showSearchSettings();
                break;
        }
    }

    private jurisdictionLinkClick() {
        this.model.Section = Sections.JURISDICTIONS;
        this.model.UserSettings.loadSettings().then(() => {
            this.jurisdictionsView.renderSettings();
        });
    }

    private searchButtonClick() {
        this.model.Section = Sections.SEARCH_SETTINGS;
        this.model.UserSettings.loadSettings().then(() => {
            this.searchSettingsView.renderSettings();
        });
    }

    private sourcelinkClick() {
        this.model.Section = Sections.SOURCELINKS;
        this.model.UserSettings.loadSettings().then(() => {
            this.model.SourceLinkSettings.loadSettings().then(() => {
                this.sourceLinkView.renderSettings();
            });
        });
        this.appEvents.trigger(AppEvents.AppInsightsSourceLinksSeleced, {Selected: true});
    }

    private backClick(e: JQueryEventObject) {
        const level = this.$(e.currentTarget).attr("data-level");
        if (!level || level === "") {
            this.model.Section = Sections.MENU;
        }
    }

    private settingsCloseClick(): void {
        this.appEvents.trigger(AppEvents.SettingsVisibilityToggled);
        $("#viewport").unbind("click");
    }

    private settingsPanelTransitionEnded(): void {
        if (!this.model.Open) {
            this.$el.hide();
        }
    }

    private addEvent(events: Backbone.EventsHash, action: browserEvent, selector: string, callBack: any): void {
        events[`${action} ${selector}`] = callBack;
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/settings/views/settings.ts